import React, { useEffect, useState } from "react";
import {
  getMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  sendMessage,
} from "../../../services/messageServices";
import { useNotification } from "../../../components/Notification/NotificationManager";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../Home/UserContext";
import { useMessage } from "../../../hooks/MessageContext";
import { FaEnvelope, FaTimes } from "react-icons/fa";
import useScrollToTop from "../../../hooks/useScollToTop";
import { MdOutgoingMail } from "react-icons/md";
import DOMPurify from "dompurify";
import Button from "../../../components/Button/Button";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import { toAbsoluteUserPhotoUrl } from "./../../../services/userPhotoServices";
import HeroImage from "../../../components/Hero/HeroImage";

const MessagerieJudoka = () => {
  useScrollToTop();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { unreadMessageCount, setUnreadMessageCount } = useMessage();
  const { addNotification } = useNotification();
  const { user } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const navigate = useNavigate();

  // Modale de réponse
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [replyRecipientId, setReplyRecipientId] = useState(null);
  const [replySubject, setReplySubject] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [messageType, setMessageType] = useState(null);
  const [approvedAdherents, setApprovedAdherents] = useState([]);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        if (!user || !user.userId) {
          console.error("Utilisateur non trouvé ou userId manquant");
          addNotification("Erreur : utilisateur non trouvé.", "error");
          setLoading(false);
          return;
        }
        const messagesData = await getMessagesByUser(user.userId);
        setMessages(messagesData);

        const unreadCount = messagesData.filter(
          (message) => !message.read_message
        ).length;
        setUnreadMessageCount(unreadCount);
      } catch (error) {
        console.error("Erreur lors de la récupération des messages :", error);
        addNotification(
          "Erreur lors de la récupération des messages.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
    fetchAdherents();
  }, [user, setUnreadMessageCount]);

  const fetchAdherents = async () => {
    try {
      if (!user?.userId) {
        addNotification("Aucun utilisateur connecté.", "error");
        return;
      }
      // Charge ici la liste des adhérents approuvés si besoin
      // setApprovedAdherents(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des adhérents :", error);
      addNotification("Erreur lors de la récupération des adhérents.", "error");
    }
  };

  const toggleReadStatus = async (messageId) => {
    try {
      await markMessageAsRead(messageId);

      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.id === messageId
            ? { ...message, read_message: !message.read_message }
            : message
        )
      );

      setUnreadMessageCount((currentCount) =>
        messages.find((m) => m.id === messageId)?.read_message
          ? currentCount + 1
          : currentCount - 1
      );
    } catch (error) {
      console.error("Erreur lors du basculement de l'état de lecture :", error);
      addNotification(
        "Erreur lors du basculement de l'état de lecture.",
        "error"
      );
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessageById(messageId);
      setMessages((prevMessages) =>
        prevMessages.filter((message) => message.id !== messageId)
      );
      addNotification("Message supprimé avec succès.", "success");

      const deletedMessage = messages.find((m) => m.id === messageId);
      if (deletedMessage && !deletedMessage.read_message) {
        setUnreadMessageCount((currentCount) => currentCount - 1);
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du message :", error);
      addNotification("Erreur lors de la suppression du message.", "error");
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const toggleModal = (type = null) => {
    setIsModalOpen((s) => !s);
    setMessageContent("");
    setMessageSubject("");
    setMessageType(type);
  };

  const handleSendMessage = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      if (!messageSubject || !messageContent) {
        addNotification(
          "Le sujet et le contenu du message sont requis.",
          "error"
        );
        return;
      }

      let recipientIds = [];
      if (messageType === "admin") {
        recipientIds = [1]; // ID de l'administrateur
      } else if (messageType === "judokas") {
        recipientIds = approvedAdherents.map((adherent) => adherent.id);
      }

      await Promise.all(
        recipientIds.map((recipientId) =>
          sendMessage({
            sender_id: user.userId,
            recipient_id: recipientId,
            subject: messageSubject,
            content: messageContent,
          })
        )
      );

      addNotification("Message envoyé avec succès.", "success");
      toggleModal();
    } catch (error) {
      console.error("Erreur lors de l'envoi du message :", error);
      addNotification("Erreur lors de l'envoi du message.", "error");
    }
  };

  const openReplyModal = (recipientId, subject) => {
    setReplyRecipientId(recipientId);
    setReplySubject(`Re: ${subject}`);
    setReplyContent("");
    setReplyModalOpen(true);
  };

  const closeReplyModal = () => {
    setReplyModalOpen(false);
    setReplyRecipientId(null);
    setReplySubject("");
    setReplyContent("");
  };

  const handleSendReply = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    try {
      if (!replySubject || !replyContent) {
        addNotification(
          "Le sujet et le contenu du message sont requis.",
          "error"
        );
        return;
      }

      await sendMessage({
        sender_id: user.userId,
        recipient_id: replyRecipientId,
        subject: replySubject,
        content: replyContent,
      });

      addNotification("Réponse envoyée avec succès.", "success");
      closeReplyModal();
    } catch (error) {
      console.error("Erreur lors de l'envoi de la réponse :", error);
      addNotification("Erreur lors de l'envoi de la réponse.", "error");
    }
  };

  const HeaderBreadcrumb = () => (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600" />
      <div className="relative text-white p-6 max-w-[1280px] mx-auto">
        <p className="text-sm/6 opacity-90 font-satoshi">
          <span
            onClick={() => handleNavigation("/")}
            className="hover:underline cursor-pointer"
          >
            Accueil
          </span>{" "}
          &gt; Votre Compte &gt; Messagerie
        </p>
        <h1 className="text-2xl md:text-3xl font-bold mt-1">Messagerie</h1>
      </div>
    </div>
  );

  return (
    <div className="w-full pt-[70px]">
          {/* HERO UNIFORMISÉ (tu choisis la photo) */}
          <HeroImage
            title="Messagerie"
            subtitle="Vous pouvez envoyer des messages à la Ligue NA et répondre aux messages reçus"
            imageUrl="/img/cover/cover_messageriejudoka.webp"
          />

      <div className="mt-4 flex flex-wrap justify-center md:justify-end max-w-[1280px] mx-auto gap-4 pr-4 pt-2">
        <button
          onClick={() => toggleModal("admin")}
          className="px-4 py-2 bg-blueprimary text-white rounded-lg hover:bg-blueprimarylight"
        >
          Envoyer un message à la Ligue NA
        </button>
      </div>

      {/* Liste des messages en cartes */}
      <div className="mt-4 max-w-[1280px] mx-auto p-4">
        <h2 className="text-xl font-semibold mb-4 font-satoshi flex items-center space-x-2">
          <div className="relative flex items-center">
            <span className="text-blueprimary text-3xl">
              <FaEnvelope />
            </span>
            {unreadMessageCount > 0 && (
              <span className="absolute -top-2 -right-3 bg-red-600 text-white text-xs rounded-full px-2 py-0.5">
                {unreadMessageCount}
              </span>
            )}
          </div>
          <span className="ml-4 font-satoshi">Messages reçus</span>
        </h2>

        {loading ? (
          <p>Chargement...</p>
        ) : messages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 font-satoshi">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`border-l-8 ${
                  message.read_message
                    ? "border-green-500"
                    : "border-blueprimary"
                } bg-white dark:bg-gray-800 shadow-md p-4 rounded-lg flex flex-col justify-between`}
              >
                <div>
                  <div className="flex justify-between items-center">
                    <img
                      src={message.Sender ? toAbsoluteUserPhotoUrl(message.Sender.photoURL) : "/img/user_avatar.jpg"}
                      alt={`${message.Sender?.prenom || "Utilisateur"} ${
                        message.Sender?.nom || ""
                      }`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                      {message.Sender
                        ? `${message.Sender.prenom} ${message.Sender.nom}`
                        : "Administrateur"}
                    </h3>
                    <span className="text-sm text-gray-500">
                      {new Date(message.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </div>
                  <h4 className="text-blueprimary font-semibold mt-2">
                    {message.subject || "Sans sujet"}
                  </h4>
                  <p
                    className="text-gray-700 dark:text-gray-300 mt-2"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        message.content || "Message vide"
                      ),
                    }}
                  ></p>
                </div>

                <div className="flex justify-between items-center mt-4">
                  {message.read_message ? (
                    <button
                      onClick={() => toggleReadStatus(message.id)}
                      className="text-blue-500 hover:underline"
                    >
                      Marquer comme non lu
                    </button>
                  ) : (
                    <button
                      onClick={() => toggleReadStatus(message.id)}
                      className="text-green-500 hover:underline"
                    >
                      Marquer comme lu
                    </button>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        openReplyModal(message.sender_id, message.subject)
                      }
                      className="text-blue-500 hover:underline"
                    >
                      Répondre
                    </button>
                    <button
                      onClick={() => handleDeleteMessage(message.id)}
                      className="text-red-500 hover:underline"
                    >
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p>Aucun message reçu.</p>
        )}
      </div>

      {/* =========================
          Modale CRÉATION (nouveau style)
         ========================= */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => toggleModal(null)}
        size="md"
        labelledById="new-message-title"
        describedById="new-message-desc"
        initialFocusSelector="#new-message-subject"
      >
        <ModalHeader
          id="new-message-title"
          title={
            messageType === "admin"
              ? "Message pour l’administrateur"
              : "Nouveau message"
          }
          subtitle="Renseignez le sujet et le contenu de votre message"
          onClose={() => toggleModal(null)}
        />
        <ModalBody id="new-message-desc">
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div>
              <label
                htmlFor="new-message-subject"
                className="text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Sujet
              </label>
              <input
                id="new-message-subject"
                type="text"
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                required
                data-autofocus
                autoComplete="off" // ← ajouté
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                placeholder="Sujet du message"
              />

              <textarea
                id="new-message-content"
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                required
                rows={6}
                autoComplete="off" // ← ajouté (utile sur certains navigateurs)
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                placeholder="Votre message…"
              />
            </div>

            <ModalFooter>
              <Button
                text="Annuler"
                onClick={() => toggleModal(null)}
                icon={FaTimes}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button
                text="Envoyer"
                icon={MdOutgoingMail}
                type="submit"
                className="!rounded-xl gap-2"
              />
            </ModalFooter>
          </form>
        </ModalBody>
      </Modal>

      {/* =========================
          Modale RÉPONSE (nouveau style)
         ========================= */}
      <Modal
        isOpen={replyModalOpen}
        onClose={closeReplyModal}
        size="md"
        labelledById="reply-message-title"
        describedById="reply-message-desc"
        initialFocusSelector="#reply-message-content"
      >
        <ModalHeader
          id="reply-message-title"
          title="Répondre au message"
          subtitle="Renseignez le sujet et votre réponse"
          onClose={closeReplyModal}
        />
        <ModalBody id="reply-message-desc">
          <form onSubmit={handleSendReply} className="space-y-4">
            <div>
              <label
                htmlFor="reply-message-subject"
                className="text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Sujet
              </label>
              <input
                id="reply-message-subject"
                type="text"
                value={replySubject}
                onChange={(e) => setReplySubject(e.target.value)}
                required
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                placeholder="Sujet"
              />
            </div>
            <div>
              <label
                htmlFor="reply-message-content"
                className="text-sm font-medium text-slate-700 dark:text-slate-200"
              >
                Réponse
              </label>
              <textarea
                id="reply-message-content"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                required
                rows={6}
                className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2"
                placeholder="Votre réponse…"
              />
            </div>

            <ModalFooter>
              <Button
                text="Annuler"
                onClick={closeReplyModal}
                icon={FaTimes}
                type="button"
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
              />
              <Button
                text="Envoyer"
                icon={MdOutgoingMail}
                type="submit"
                className="!rounded-xl gap-2"
              />
            </ModalFooter>
          </form>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default MessagerieJudoka;
